import { useEffect, useRef, useState } from "react";

/**
 * Arama kutusundaki daktilo efektli placeholder.
 *
 * ÖNCEKİ SÜRÜMÜN SORUNU
 * ---------------------
 * Animasyon durumu (charIndex/index/isDeleting) React state'inde tutuluyor ve
 * effect'in BAĞIMLILIK listesinde yer alıyordu. Sonuç: her karakterde effect
 * sökülüp yeniden kuruluyordu ve üç ayrı kusur birikiyordu:
 *
 *   1. Yazma tamamlandığında kurulan 1500 ms'lik iç `setTimeout` cleanup'a
 *      dahil değildi. Kullanıcı arama kutusuna yazıp silince (`paused` gidip
 *      geliyor) her turda bir zamanlayıcı daha birikiyordu.
 *   2. `texts` Redux'tan sonradan doldurulduğu için eski `index` yeni diziyi
 *      aşabiliyor ve `texts[index]` `undefined` olabiliyordu.
 *   3. Zincirleme setState'ler React'in iç içe güncelleme sınırını aşıp
 *      "Maximum update depth exceeded" hatasını üretiyordu.
 *      Ölçüldü (21 Ağustos, production): /:slug sayfalarında, hatadan hemen
 *      önce breadcrumb'da bir UI Click var.
 *
 * BU SÜRÜMDE
 * ----------
 * Animasyon durumu ref'te; effect yalnızca `texts` ve `paused` değişince
 * çalışıyor, karakter başına DEĞİL. Tek bir zamanlayıcı zinciri var ve o da
 * tek değişkende tutulup cleanup'ta mutlaka temizleniyor — kaçak kalmıyor.
 * `cancelled` bayrağı, temizlenmeden önce tetiklenmiş bir callback'in zinciri
 * sürdürmesini engelliyor.
 *
 * Kritik özellik: tur başına EN FAZLA bir `setCurrent` çağrısı var ve turlar
 * arasında en az 30 ms bulunuyor. Yani iç içe güncelleme zinciri oluşması
 * yapısal olarak mümkün değil.
 */
const TYPE_MS = 80;
const DELETE_MS = 30;
const HOLD_MS = 1500;

const useTypewriterPlaceholder = (texts: string[], paused: boolean) => {
  const [current, setCurrent] = useState("");

  // Animasyon durumu — state DEĞİL, çünkü her karakterde render tetiklemesi ve
  // effect'i yeniden kurması gerekmiyor.
  const stateRef = useRef({ index: 0, charIndex: 0, deleting: false });
  // `texts` değiştiğinde baştan başlamak için: eski index yeni diziyi aşabilir.
  const lastTextsRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (lastTextsRef.current !== texts) {
      lastTextsRef.current = texts;
      stateRef.current = { index: 0, charIndex: 0, deleting: false };
      setCurrent("");
    }

    if (paused || texts.length === 0) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const s = stateRef.current;
      // Dizi küçüldüyse index taşabilir; boş dizgeye düşüp bir sonraki turda
      // kendini toparlar (aşağıdaki modulo).
      const text = texts[s.index] ?? "";

      if (!s.deleting) {
        if (s.charIndex < text.length) {
          s.charIndex += 1;
          setCurrent(text.slice(0, s.charIndex));
          timer = setTimeout(tick, TYPE_MS);
        } else {
          // Yazma bitti: sil moduna geç ve bekle. Bu zamanlayıcı da aynı
          // `timer` değişkeninde tutuluyor, yani cleanup onu da temizler.
          s.deleting = true;
          timer = setTimeout(tick, HOLD_MS);
        }
      } else {
        if (s.charIndex > 0) {
          s.charIndex -= 1;
          setCurrent(text.slice(0, s.charIndex));
          timer = setTimeout(tick, DELETE_MS);
        } else {
          s.deleting = false;
          s.index = (s.index + 1) % texts.length;
          timer = setTimeout(tick, TYPE_MS);
        }
      }
    };

    timer = setTimeout(tick, TYPE_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [texts, paused]);

  return current;
};

export default useTypewriterPlaceholder;
